// Shown once, to a Microsoft sign-in that has no workspace and no invitation: a self-serve sign-up names its
// company here (Microsoft's sign-up page asks only for a name). One field, the terms, and the workspace is
// created with the signed-in person as its Admin.
import { useState } from "react";
import { useAuth } from "../auth/AuthProvider";
import { overline } from "../shared/components";
import { Button, Checkbox, Icon, Input } from "../ui";

export function CreateWorkspace({ email, busy, onCreate, onSwitch }: { email: string; busy: boolean; onCreate: (company: string) => void; onSwitch: () => void }) {
  const auth = useAuth();
  const [company, setCompany] = useState("");
  const [terms, setTerms] = useState(false);
  const [tried, setTried] = useState(false);
  const siteUrl = auth.config.environment === "prod" ? "https://www.autovouch.app" : `https://www.${auth.config.environment}.autovouch.app`;
  const companyProblem = company.trim().length < 2 ? "Which company is this for?" : "";
  const termsProblem = terms ? "" : "Please accept the terms to continue";

  const submit = (ev: React.FormEvent) => {
    ev.preventDefault();
    setTried(true);
    if (companyProblem || termsProblem) return;
    onCreate(company.trim());
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(320px,.8fr) minmax(0,1.2fr)", minHeight: "100vh", background: "#fff" }} className="avp-invite">
      <aside style={{ position: "relative", overflow: "hidden", background: "var(--gradient-glow),var(--gradient-deep)", color: "#fff", padding: "44px 52px", display: "flex", flexDirection: "column", justifyContent: "space-between", gap: 24 }}>
        <a href={siteUrl} style={{ alignSelf: "flex-start", display: "flex" }}><img src="/logo-wordmark-inverse.png" alt="AutoVouch" style={{ height: 42 }} /></a>
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <div style={{ ...overline, color: "var(--av-cyan-400)" }}>One more thing</div>
          <h2 style={{ font: "800 clamp(34px,3.4vw,46px)/1.05 var(--font-display)", letterSpacing: "-0.02em", margin: 0, color: "#fff" }}>Vouched<br />on arrival.</h2>
          <p style={{ margin: 0, font: "600 16px/1.5 var(--font-body)", color: "var(--av-sky)" }}>Your workspace starts in preview: AutoVouch shows what it would do and changes nothing in Syncore until you go live.</p>
        </div>
        <span style={{ font: "13px var(--font-body)", color: "var(--av-sky)" }}>Syncore is a trademark of Facilisgroup, LLC.</span>
      </aside>
      <main style={{ display: "flex", flexDirection: "column", padding: "28px clamp(24px,5vw,64px)" }}>
        <header style={{ display: "flex", alignItems: "center", gap: 16, minHeight: 40 }}>
          <span style={{ flex: 1 }} />
          <span style={{ font: "var(--type-body-sm)", color: "var(--text-muted)" }}>Signed in as <b>{email}</b>. <a onClick={onSwitch} style={{ cursor: "pointer" }}>Switch account</a></span>
        </header>
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "32px 0" }}>
          <form onSubmit={submit} noValidate style={{ width: "100%", maxWidth: 460, display: "flex", flexDirection: "column", gap: 28, animation: "av-rise var(--dur-slow) var(--ease-out)" }}>
            <div>
              <h1 style={{ font: "800 34px/1.1 var(--font-display)", letterSpacing: "-0.02em", margin: 0 }}>Name your workspace</h1>
              <p style={{ font: "17px/1.5 var(--font-body)", color: "var(--text-muted)", margin: "10px 0 0" }}>Which company will AutoVouch check invoices for? You’ll be its administrator.</p>
            </div>
            <Input label="Company" value={company} onChange={(ev) => setCompany(ev.target.value)} placeholder="Northwind Promo, Inc." autoFocus error={tried ? companyProblem || undefined : undefined} />
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <Checkbox checked={terms} onChange={setTerms} label={<span>I agree to the <a href={`${siteUrl}/terms`} target="_blank" rel="noreferrer">Terms of Service</a> and <a href={`${siteUrl}/privacy`} target="_blank" rel="noreferrer">Privacy Policy</a></span>} />
              {tried && termsProblem && <span style={{ font: "var(--type-body-sm)", color: "var(--status-mismatch-fg)" }}>{termsProblem}</span>}
            </div>
            <div><Button type="submit" size="lg" loading={busy} trailingIcon={<Icon name="arrow-right" size={18} />}>{busy ? "Creating…" : "Create workspace"}</Button></div>
          </form>
        </div>
      </main>
    </div>
  );
}
